import React, { useEffect } from 'react';

interface SEOOptimizerProps {
  page?: 'home' | 'blog' | 'services' | 'contact';
  language?: 'it' | 'es' | 'en';
  articleData?: {
    title?: string;
    description?: string;
    keywords?: string;
    category?: string;
    author?: string;
    publishDate?: string;
    modifiedDate?: string;
    image?: string;
    slug?: string;
  };
}

const SEOOptimizer: React.FC<SEOOptimizerProps> = ({ 
  page = 'home', 
  language = 'it',
  articleData 
}) => {
  
  useEffect(() => {
    // Enhanced SEO Meta Tags Implementation
    const updateMetaTags = () => {
      const seoData = getSEOData(page, language, articleData);
      
      // Update title
      document.title = seoData.title;
      
      // Update meta description
      updateMetaTag('name', 'description', seoData.description);
      
      // Update meta keywords
      updateMetaTag('name', 'keywords', seoData.keywords);
      
      // Update canonical URL
      updateCanonicalURL(seoData.canonicalUrl);
      
      // Update Open Graph tags
      updateMetaTag('property', 'og:title', seoData.title);
      updateMetaTag('property', 'og:description', seoData.description);
      updateMetaTag('property', 'og:image', seoData.image);
      updateMetaTag('property', 'og:url', seoData.canonicalUrl);
      updateMetaTag('property', 'og:type', seoData.ogType);
      updateMetaTag('property', 'og:locale', seoData.locale);
      updateMetaTag('property', 'og:site_name', 'Residencia Panama');
      
      // Update Twitter Card tags
      updateMetaTag('name', 'twitter:card', 'summary_large_image');
      updateMetaTag('name', 'twitter:title', seoData.title);
      updateMetaTag('name', 'twitter:description', seoData.description);
      updateMetaTag('name', 'twitter:image', seoData.image);
      
      // Update article-specific tags
      if (page === 'blog' && articleData) {
        updateMetaTag('property', 'article:author', articleData.author || 'RS International Services');
        updateMetaTag('property', 'article:published_time', articleData.publishDate || new Date().toISOString());
        updateMetaTag('property', 'article:modified_time', articleData.modifiedDate || new Date().toISOString());
        updateMetaTag('property', 'article:section', articleData.category || 'Residenza Panama');
        updateMetaTag('property', 'article:tag', seoData.keywords);
      }
      
      // Update hreflang tags for multilingual SEO
      updateHreflangTags(page, articleData?.slug);
      
      // Update structured data
      updateStructuredData(seoData, page, articleData);
    };
    
    updateMetaTags();
  }, [page, language, articleData]);

  const getSEOData = (page: string, language: string, articleData?: any) => {
    const baseUrl = 'https://residenciapanama.com';
    
    const seoTemplates = {
      home: {
        it: {
          title: 'Residenza Panama 2025 | Consulenza Specializzata per Italiani | RS International',
          description: 'Ottieni la residenza permanente a Panama con soli 5.000€ grazie al Trattato Italia-Panama. Consulenza legale specializzata, società offshore, investimenti immobiliari. Preventivo gratuito.',
          keywords: 'residenza panama italiani, trattato italia panama, società offshore panama, investimenti panama, cedula panama, cittadinanza panama, consulenza legale panama, RS international services',
          canonicalUrl: baseUrl,
          image: `${baseUrl}/images/optimized/hero/hero-0.jpg`,
          ogType: 'website',
          locale: 'it_IT'
        },
        es: {
          title: 'Residencia Panamá 2025 | Consultoría Especializada | RS International Services',
          description: 'Obtén tu residencia permanente en Panamá. Servicios legales especializados, sociedades offshore, inversiones inmobiliarias. Consulta gratuita con expertos.',
          keywords: 'residencia panama, sociedades offshore panama, inversiones panama, cedula panama, ciudadania panama, consultoria legal panama',
          canonicalUrl: `${baseUrl}/es`,
          image: `${baseUrl}/images/optimized/hero/hero-0.jpg`,
          ogType: 'website',
          locale: 'es_ES'
        },
        en: {
          title: 'Panama Residency 2025 | Specialized Legal Consulting | RS International Services',
          description: 'Get your permanent residency in Panama. Specialized legal services, offshore companies, real estate investments. Free consultation with experts.',
          keywords: 'panama residency, panama offshore companies, panama investments, panama cedula, panama citizenship, legal consulting panama',
          canonicalUrl: `${baseUrl}/en`,
          image: `${baseUrl}/images/optimized/hero/hero-0.jpg`,
          ogType: 'website',
          locale: 'en_US'
        }
      },
      blog: {
        it: {
          title: articleData?.title ? `${articleData.title} | Blog Residencia Panama` : 'Blog Residencia Panama | Guide e Consigli Esperti',
          description: articleData?.description || 'Guide complete su residenza, investimenti e opportunità fiscali a Panama. Consigli di esperti per italiani che vogliono trasferirsi.',
          keywords: articleData?.keywords || 'blog panama, guide residenza panama, investimenti panama, fiscalità panama, trasferirsi panama',
          canonicalUrl: articleData?.slug ? `${baseUrl}/blog/${articleData.slug}` : `${baseUrl}/blog`,
          image: articleData?.image || `${baseUrl}/images/optimized/hero/hero-1.jpg`,
          ogType: 'article',
          locale: 'it_IT'
        },
        es: {
          title: articleData?.title ? `${articleData.title} | Blog Residencia Panamá` : 'Blog Residencia Panamá | Guías y Consejos de Expertos',
          description: articleData?.description || 'Guías completas sobre residencia, inversiones y oportunidades fiscales en Panamá. Consejos de expertos.',
          keywords: articleData?.keywords || 'blog panama, guias residencia panama, inversiones panama, fiscalidad panama',
          canonicalUrl: articleData?.slug ? `${baseUrl}/es/blog/${articleData.slug}` : `${baseUrl}/es/blog`,
          image: articleData?.image || `${baseUrl}/images/optimized/hero/hero-1.jpg`,
          ogType: 'article',
          locale: 'es_ES'
        },
        en: {
          title: articleData?.title ? `${articleData.title} | Residencia Panama Blog` : 'Residencia Panama Blog | Expert Guides and Advice',
          description: articleData?.description || 'Complete guides on residency, investments and tax opportunities in Panama. Expert advice for international clients.',
          keywords: articleData?.keywords || 'panama blog, panama residency guides, panama investments, panama taxation',
          canonicalUrl: articleData?.slug ? `${baseUrl}/en/blog/${articleData.slug}` : `${baseUrl}/en/blog`,
          image: articleData?.image || `${baseUrl}/images/optimized/hero/hero-1.jpg`,
          ogType: 'article',
          locale: 'en_US'
        }
      },
      services: {
        it: {
          title: 'Servizi Residencia Panama | Residenza, Società Offshore, Investimenti | RS International',
          description: 'Servizi completi per residenza Panama: costituzione società offshore, apertura conti bancari, investimenti immobiliari, consulenza fiscale. Team di esperti legali.',
          keywords: 'servizi panama, società offshore panama, residenza panama servizi, investimenti panama, consulenza legale panama, apertura conti panama',
          canonicalUrl: `${baseUrl}/servizi`,
          image: `${baseUrl}/images/optimized/office/office-0.jpg`,
          ogType: 'website',
          locale: 'it_IT'
        },
        es: {
          title: 'Servicios Residencia Panamá | Residencia, Sociedades Offshore, Inversiones',
          description: 'Servicios completos para residencia Panamá: constitución sociedades offshore, apertura cuentas bancarias, inversiones inmobiliarias.',
          keywords: 'servicios panama, sociedades offshore panama, residencia panama servicios, inversiones panama',
          canonicalUrl: `${baseUrl}/es/servicios`,
          image: `${baseUrl}/images/optimized/office/office-0.jpg`,
          ogType: 'website',
          locale: 'es_ES'
        },
        en: {
          title: 'Panama Services | Residency, Offshore Companies, Investments | RS International',
          description: 'Complete Panama services: offshore company formation, bank account opening, real estate investments, tax consulting.',
          keywords: 'panama services, panama offshore companies, panama residency services, panama investments',
          canonicalUrl: `${baseUrl}/en/services`,
          image: `${baseUrl}/images/optimized/office/office-0.jpg`,
          ogType: 'website',
          locale: 'en_US'
        }
      },
      contact: {
        it: {
          title: 'Contatti Residencia Panama | Consulenza Gratuita | RS International Services',
          description: 'Contatta i nostri esperti per una consulenza gratuita su residenza Panama. Uffici a Panama City e Milano. Risposta garantita in 24h.',
          keywords: 'contatti residenza panama, consulenza gratuita panama, uffici panama, RS international services contatti',
          canonicalUrl: `${baseUrl}/contatti`,
          image: `${baseUrl}/images/optimized/office/office-1.jpg`,
          ogType: 'website',
          locale: 'it_IT'
        },
        es: {
          title: 'Contactos Residencia Panamá | Consulta Gratuita | RS International Services',
          description: 'Contacta nuestros expertos para una consulta gratuita sobre residencia Panamá. Oficinas en Ciudad de Panamá.',
          keywords: 'contactos residencia panama, consulta gratuita panama, oficinas panama',
          canonicalUrl: `${baseUrl}/es/contactos`,
          image: `${baseUrl}/images/optimized/office/office-1.jpg`,
          ogType: 'website',
          locale: 'es_ES'
        },
        en: {
          title: 'Contact Residencia Panama | Free Consultation | RS International Services',
          description: 'Contact our experts for a free consultation on Panama residency. Offices in Panama City and Milan.',
          keywords: 'contact residencia panama, free consultation panama, panama offices',
          canonicalUrl: `${baseUrl}/en/contact`,
          image: `${baseUrl}/images/optimized/office/office-1.jpg`,
          ogType: 'website',
          locale: 'en_US'
        }
      }
    };
    
    return seoTemplates[page as keyof typeof seoTemplates]?.[language as keyof typeof seoTemplates.home] || seoTemplates.home.it;
  };

  const updateMetaTag = (attribute: string, name: string, content: string) => {
    if (!content) return;
    
    let meta = document.querySelector(`meta[${attribute}="${name}"]`);
    if (meta) {
      meta.setAttribute('content', content);
    } else {
      meta = document.createElement('meta');
      meta.setAttribute(attribute, name);
      meta.setAttribute('content', content);
      document.head.appendChild(meta);
    }
  };

  const updateCanonicalURL = (url: string) => {
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', url);
    } else {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      canonical.setAttribute('href', url);
      document.head.appendChild(canonical);
    }
  };

  const updateHreflangTags = (page: string, slug?: string) => {
    // Remove existing hreflang tags
    const existingHreflang = document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingHreflang.forEach(tag => tag.remove());
    
    const baseUrl = 'https://residenciapanama.com';
    const languages = ['it', 'es', 'en'];
    
    languages.forEach(lang => {
      const hreflang = document.createElement('link');
      hreflang.setAttribute('rel', 'alternate');
      hreflang.setAttribute('hreflang', lang);
      
      let href = baseUrl;
      if (lang !== 'it') href += `/${lang}`;
      if (page === 'blog' && slug) href += `/blog/${slug}`;
      else if (page !== 'home') href += `/${page}`;
      
      hreflang.setAttribute('href', href);
      document.head.appendChild(hreflang);
    });
    
    // Add x-default hreflang
    const xDefault = document.createElement('link');
    xDefault.setAttribute('rel', 'alternate');
    xDefault.setAttribute('hreflang', 'x-default');
    xDefault.setAttribute('href', baseUrl);
    document.head.appendChild(xDefault);
  };

  const updateStructuredData = (seoData: any, page: string, articleData?: any) => {
    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"][data-seo-optimizer]');
    if (existingScript) {
      existingScript.remove();
    }

    let structuredData: any = {};

    if (page === 'home') {
      structuredData = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            "@id": "https://residenciapanama.com/#organization",
            "name": "RS International Services",
            "url": "https://residenciapanama.com",
            "logo": {
              "@type": "ImageObject",
              "url": "https://residenciapanama.com/logo.png",
              "width": 300,
              "height": 100
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+507-6958-4333",
              "contactType": "customer service",
              "availableLanguage": ["Italian", "Spanish", "English"]
            },
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Avenida 10 Norte  Calle Tael Casa C1 Urbanizacion Los Angeles Panama City",
              "addressLocality": "Panama City",
              "addressRegion": "Panama",
              "postalCode": "0801",
              "addressCountry": "PA"
            },
            "sameAs": [
              "https://www.linkedin.com/company/rs-international-services",
              "https://www.facebook.com/residenciapanama"
            ]
          },
          {
            "@type": "WebSite",
            "@id": "https://residenciapanama.com/#website",
            "url": "https://residenciapanama.com",
            "name": "Residencia Panama",
            "description": seoData.description,
            "publisher": {
              "@id": "https://residenciapanama.com/#organization"
            },
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://residenciapanama.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          },
          {
            "@type": "LegalService",
            "@id": "https://residenciapanama.com/#service",
            "name": "Residencia Panama Legal Services",
            "description": "Specialized legal services for Panama residency, offshore companies, and investments",
            "provider": {
              "@id": "https://residenciapanama.com/#organization"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Panama"
            },
            "serviceType": [
              "Panama Residency Services",
              "Offshore Company Formation",
              "Real Estate Investment",
              "Tax Consulting",
              "Banking Services"
            ]
          }
        ]
      };
    } else if (page === 'blog' && articleData) {
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": articleData.title,
        "description": seoData.description,
        "image": {
          "@type": "ImageObject",
          "url": seoData.image,
          "width": 1200,
          "height": 630
        },
        "author": {
          "@type": "Organization",
          "name": articleData.author || "RS International Services",
          "url": "https://residenciapanama.com"
        },
        "publisher": {
          "@type": "Organization",
          "name": "RS International Services",
          "logo": {
            "@type": "ImageObject",
            "url": "https://residenciapanama.com/logo.png",
            "width": 300,
            "height": 100
          }
        },
        "datePublished": articleData.publishDate || new Date().toISOString(),
        "dateModified": articleData.modifiedDate || new Date().toISOString(),
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": seoData.canonicalUrl
        },
        "articleSection": articleData.category || "Panama Services",
        "keywords": seoData.keywords,
        "inLanguage": seoData.locale.replace('_', '-'),
        "about": {
          "@type": "Thing",
          "name": "Panama Residency and Investment Services"
        }
      };
    }

    if (Object.keys(structuredData).length > 0) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-optimizer', 'true');
      script.textContent = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  };

  // Component doesn't render anything visible
  return null;
};

export { SEOOptimizer };
export default SEOOptimizer;